import type { Action, Reducer } from 'redux';

import { IAppThunkAction, IGoogleTagManagerEvent } from '.';
import { IRewardFlow } from '../interfaces/IRewardFlow';
import RewardsService from '../service/rewards';
import { appInsights } from '../Logging/appInsights';
import { GoogleTagManagerEvents } from '../helpers/googleTagManager';

// --- State ---

export interface IRewardsState {
    flows: IRewardFlow[];
    flowsLoading: boolean;
    flowsLoaded: boolean;
    flowsSiteId?: number;

    upsellWasShown: boolean;
    initialPlayerCount?: number;
}

// --- Action Types ---

export const REWARD_FLOWS_LOADING: 'REWARD_FLOWS_LOADING' = 'REWARD_FLOWS_LOADING';
export const REWARD_FLOWS_LOADED: 'REWARD_FLOWS_LOADED' = 'REWARD_FLOWS_LOADED';
export const REWARD_FLOWS_FAILED: 'REWARD_FLOWS_FAILED' = 'REWARD_FLOWS_FAILED';
export const UPSELL_WAS_SHOWN: 'UPSELL_WAS_SHOWN' = 'UPSELL_WAS_SHOWN';
export const UPSELL_INITIAL_COUNT: 'UPSELL_INITIAL_COUNT' = 'UPSELL_INITIAL_COUNT';
export const UPSELL_RESET: 'UPSELL_RESET' = 'UPSELL_RESET';
export const UPSELL_PLAYERS_UPDATED: 'UPSELL_PLAYERS_UPDATED' = 'UPSELL_PLAYERS_UPDATED';
export const UPSELL_OFFER_ACCEPTED: 'UPSELL_OFFER_ACCEPTED' = 'UPSELL_OFFER_ACCEPTED';
export const UPSELL_CARD_EXPANDED: 'UPSELL_CARD_EXPANDED' = 'UPSELL_CARD_EXPANDED';

// --- Action Interfaces ---

interface IRewardFlowsLoadingAction extends Action {
    type: typeof REWARD_FLOWS_LOADING;
    siteId: number;
}

interface IRewardFlowsLoadedAction extends Action {
    type: typeof REWARD_FLOWS_LOADED;
    flows: IRewardFlow[];
    siteId: number;
}

interface IRewardFlowsFailedAction extends Action {
    type: typeof REWARD_FLOWS_FAILED;
}

interface IUpsellWasShownAction extends Action {
    type: typeof UPSELL_WAS_SHOWN;
    googleTagManagerEvent: Array<IGoogleTagManagerEvent>;
}

interface IUpsellInitialCountAction extends Action {
    type: typeof UPSELL_INITIAL_COUNT;
    initialPlayerCount: number;
}

interface IUpsellResetAction extends Action {
    type: typeof UPSELL_RESET;
}

interface IUpsellPlayersUpdatedAction extends Action {
    type: typeof UPSELL_PLAYERS_UPDATED;
    googleTagManagerEvent: Array<IGoogleTagManagerEvent>;
}

interface IUpsellOfferAcceptedAction extends Action {
    type: typeof UPSELL_OFFER_ACCEPTED;
    googleTagManagerEvent: Array<IGoogleTagManagerEvent>;
}

interface IUpsellCardExpandedAction extends Action {
    type: typeof UPSELL_CARD_EXPANDED;
    googleTagManagerEvent: Array<IGoogleTagManagerEvent>;
}

export type KnownRewardsAction =
    | IRewardFlowsLoadingAction
    | IRewardFlowsLoadedAction
    | IRewardFlowsFailedAction
    | IUpsellWasShownAction
    | IUpsellInitialCountAction
    | IUpsellResetAction
    | IUpsellPlayersUpdatedAction
    | IUpsellOfferAcceptedAction
    | IUpsellCardExpandedAction;

// --- AbortController tracking ---

let inFlightLoadFlows: AbortController | null = null;

// --- Action Creators ---

export const actionCreators = {
    loadRewardFlows:
        (siteId: number): IAppThunkAction<KnownRewardsAction> =>
        (dispatch, getState) => {
            const { flowsSiteId, flowsLoaded, flowsLoading } = getState().rewards;
            if (flowsSiteId === siteId && (flowsLoaded || flowsLoading)) return;

            if (inFlightLoadFlows) inFlightLoadFlows.abort();

            const controller = new AbortController();
            inFlightLoadFlows = controller;

            dispatch({ type: REWARD_FLOWS_LOADING, siteId });

            RewardsService.GetSiteRewardFlows(siteId, controller.signal)
                .then((flows) => {
                    dispatch({ type: REWARD_FLOWS_LOADED, flows, siteId });
                })
                .catch((error: Error) => {
                    if ((error as any)?.name === 'AbortError') {
                        console.log('Cancelled in-flight reward flows request', error);
                        return;
                    }

                    console.error('Failed to load reward flows', error);
                    appInsights?.trackException(
                        { error: new Error('Failed to load reward flows'), severityLevel: 3 },
                        { exception: error, sourceFile: 'Rewards.ts', product: 'booking' }
                    );

                    dispatch({ type: REWARD_FLOWS_FAILED });
                })
                .finally(() => {
                    if (inFlightLoadFlows === controller) inFlightLoadFlows = null;
                });
        },

    upsellWasShown: (variant: string, currentPlayers: number, minimumPlayers: number) =>
        ({
            type: UPSELL_WAS_SHOWN,
            googleTagManagerEvent: GoogleTagManagerEvents.UpsellShown(variant, currentPlayers, minimumPlayers),
        } as IUpsellWasShownAction),

    upsellInitialCount: (initialPlayerCount: number) =>
        ({ type: UPSELL_INITIAL_COUNT, initialPlayerCount } as IUpsellInitialCountAction),

    upsellReset: () => ({ type: UPSELL_RESET } as IUpsellResetAction),

    upsellPlayersUpdated: (previousPlayers: number, newPlayers: number, minimumPlayers: number, offerMet: boolean) =>
        ({
            type: UPSELL_PLAYERS_UPDATED,
            googleTagManagerEvent: GoogleTagManagerEvents.UpsellPlayersUpdated(previousPlayers, newPlayers, minimumPlayers, offerMet),
        } as IUpsellPlayersUpdatedAction),

    upsellOfferAccepted: (previousPlayers: number, newPlayers: number, minimumPlayers: number) =>
        ({
            type: UPSELL_OFFER_ACCEPTED,
            googleTagManagerEvent: GoogleTagManagerEvents.UpsellOfferAccepted(previousPlayers, newPlayers, minimumPlayers),
        } as IUpsellOfferAcceptedAction),

    upsellCardExpanded: () =>
        ({
            type: UPSELL_CARD_EXPANDED,
            googleTagManagerEvent: GoogleTagManagerEvents.UpsellCardExpanded(),
        } as IUpsellCardExpandedAction),
};

// --- Reducer ---

const undefinedState: IRewardsState = {
    flows: [],
    flowsLoading: false,
    flowsLoaded: false,
    flowsSiteId: undefined,
    upsellWasShown: false,
    initialPlayerCount: undefined,
};

export const reducer: Reducer<IRewardsState> = (
    state: IRewardsState | undefined,
    incomingAction: Action
): IRewardsState => {
    if (state === undefined) return undefinedState;

    const action = incomingAction as KnownRewardsAction;
    switch (action.type) {
        case REWARD_FLOWS_LOADING:
            return {
                ...state,
                flowsLoading: true,
                flowsLoaded: false,
                flowsSiteId: action.siteId,
            };
        case REWARD_FLOWS_LOADED:
            return {
                ...state,
                flows: action.flows,
                flowsLoading: false,
                flowsLoaded: true,
                flowsSiteId: action.siteId,
            };
        case REWARD_FLOWS_FAILED:
            return {
                ...state,
                flows: [],
                flowsLoading: false,
                flowsLoaded: false,
            };
        case UPSELL_WAS_SHOWN:
            return {
                ...state,
                upsellWasShown: true,
            };
        case UPSELL_INITIAL_COUNT:
            return {
                ...state,
                initialPlayerCount: action.initialPlayerCount,
            };
        case UPSELL_RESET:
            return {
                ...state,
                upsellWasShown: false,
                initialPlayerCount: undefined,
            };
        case UPSELL_PLAYERS_UPDATED:
        case UPSELL_OFFER_ACCEPTED:
        case UPSELL_CARD_EXPANDED:
            return state;
        default:
            return state;
    }
};
